import { useEffect, useRef, useState } from 'react'

/**
 * Tek parça tarih aralığı seçici — PnL Takip'in "Başlangıç/Bitiş" input çifti
 * yerine (kullanıcı isteği 2026-08-01: yazıyla giriş olmasın, tıklayınca
 * takvim açılsın, aralık tek bileşenden seçilsin).
 *
 * Akış: kutuya tıkla → takvim açılır → ilk tıklama başlangıcı, ikincisi
 * bitişi seçer, aralık ANINDA uygulanır ve takvim kapanır. Başlangıçtan
 * önceki bir güne tıklanırsa seçim o günden yeniden başlar. `maxDays`
 * sunucunun MAX_DAYS sınırının aynısı — aşan günler tıklanamaz olur ki
 * sunucudan 400 yemeden sınır takvimde görünsün.
 */

const AYLAR = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
]
//: Pazartesi başlangıçlı hafta — tahta günü pazartesiyle açılır okunur.
const HAFTA = ['Pt', 'Sa', 'Ça', 'Pe', 'Cu', 'Ct', 'Pa']

function isoYap(yil: number, ayIdx: number, gun: number): string {
  return `${yil}-${String(ayIdx + 1).padStart(2, '0')}-${String(gun).padStart(2, '0')}`
}

/** YYYY-MM-DD → GG.AA.YYYY (PnL sayfasıyla aynı biçim). */
function goster(iso: string): string {
  const [y, m, d] = iso.split('-')
  return `${d}.${m}.${y}`
}

/** İki ISO gün arasındaki takvim günü farkı (b − a). Saf gün, saat oynamaz. */
function gunFarki(a: string, b: string): number {
  return Math.round((Date.parse(b) - Date.parse(a)) / 86_400_000)
}

export function DateRangePicker({
  since,
  until,
  onChange,
  maxDays = 92,
}: {
  since: string
  until: string
  onChange: (since: string, until: string) => void
  maxDays?: number
}) {
  const [acik, setAcik] = useState(false)
  /** Bekleyen başlangıç — ilk tıklamadan ikincisine kadar dolu. */
  const [bas, setBas] = useState<string | null>(null)
  /** Fare hangi günün üstünde — bekleyen seçimde aralık önizlemesi. */
  const [ustunde, setUstunde] = useState<string | null>(null)
  const [ay, setAy] = useState(() => until.slice(0, 7)) // görüntülenen YYYY-MM
  const kok = useRef<HTMLDivElement>(null)

  // Dışarı tıklama / Esc kapatır; bekleyen yarım seçim uygulanmadan düşer.
  useEffect(() => {
    if (!acik) return
    const disari = (e: MouseEvent) => {
      if (kok.current && !kok.current.contains(e.target as Node)) setAcik(false)
    }
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAcik(false)
    }
    document.addEventListener('mousedown', disari)
    document.addEventListener('keydown', esc)
    return () => {
      document.removeEventListener('mousedown', disari)
      document.removeEventListener('keydown', esc)
    }
  }, [acik])

  const acKapa = () => {
    setAy(until.slice(0, 7))       // açılışta mevcut aralığın bitiş ayı görünsün
    setBas(null)
    setUstunde(null)
    setAcik((v) => !v)
  }

  const yil = parseInt(ay.slice(0, 4), 10)
  const ayIdx = parseInt(ay.slice(5, 7), 10) - 1
  const kaydir = (adim: number) => {
    const d = new Date(yil, ayIdx + adim, 1)
    setAy(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`)
  }

  const sec = (iso: string) => {
    if (bas === null || iso < bas) {
      setBas(iso)                  // ilk seçim ya da geriye tıklama: baştan başla
      return
    }
    onChange(bas, iso)
    setBas(null)
    setAcik(false)
  }

  // Vurgulanan aralık: seçim sürüyorsa [bas, fare], değilse uygulanmış aralık.
  const [v1, v2] =
    bas !== null
      ? [bas, ustunde !== null && ustunde >= bas ? ustunde : bas]
      : [since, until]

  const bosluk = (new Date(yil, ayIdx, 1).getDay() + 6) % 7 // Pazartesi = 0
  const gunSayisi = new Date(yil, ayIdx + 1, 0).getDate()
  const simdi = new Date()
  const bugun = isoYap(simdi.getFullYear(), simdi.getMonth(), simdi.getDate())

  return (
    <div className="drp" ref={kok}>
      <button
        type="button"
        className="drp-btn"
        onClick={acKapa}
        aria-haspopup="dialog"
        aria-expanded={acik}
        title="Tarih aralığı seç"
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="2" strokeLinecap="round"
             aria-hidden="true">
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M3 10h18M8 3v4M16 3v4" />
        </svg>
        <span>{goster(since)} – {goster(until)}</span>
      </button>
      {acik && (
        <div className="drp-pop" role="dialog" aria-label="Tarih aralığı seç">
          <div className="drp-head">
            <button type="button" className="pager-btn" onClick={() => kaydir(-1)}
                    aria-label="Önceki ay">‹</button>
            <span className="drp-title">{AYLAR[ayIdx]} {yil}</span>
            <button type="button" className="pager-btn" onClick={() => kaydir(1)}
                    aria-label="Sonraki ay">›</button>
          </div>
          <div className="drp-grid" onMouseLeave={() => setUstunde(null)}>
            {HAFTA.map((g) => (
              <span key={g} className="drp-wd">{g}</span>
            ))}
            {Array.from({ length: bosluk }, (_, i) => (
              <span key={`b${i}`} />
            ))}
            {Array.from({ length: gunSayisi }, (_, i) => {
              const iso = isoYap(yil, ayIdx, i + 1)
              const icinde = iso >= v1 && iso <= v2
              const uc = iso === v1 || iso === v2
              // Aralık sınırı: başlangıç dahil maxDays gün → fark en çok maxDays−1.
              const yasak = bas !== null && iso > bas && gunFarki(bas, iso) > maxDays - 1
              return (
                <button
                  key={iso}
                  type="button"
                  className={
                    'drp-day' +
                    (icinde ? ' drp-in' : '') +
                    (uc ? ' drp-edge' : '') +
                    (iso === bugun ? ' drp-today' : '')
                  }
                  disabled={yasak}
                  onClick={() => sec(iso)}
                  onMouseEnter={() => bas !== null && setUstunde(iso)}
                >
                  {i + 1}
                </button>
              )
            })}
          </div>
          <div className="drp-hint">
            {bas !== null ? `${goster(bas)} → bitiş gününü seç` : 'Başlangıç gününü seç'}
          </div>
        </div>
      )}
    </div>
  )
}
